import { Box } from "@chakra-ui/react";
import { differenceInSeconds } from "date-fns";
import styled, { css } from "styled-components";
import {
  useEffect,
  useState,
  useMemo
} from "react";

import {
  Conversation,
  User
} from "src/api";
import { Text } from "src/components/text";
import { theme } from "src/components/theme";

interface SessionTimerProps {
  conversation: Conversation
  partner: User | null
}

export const SessionTimer: React.FC<SessionTimerProps> = ({ conversation, partner }) => {
  const startTime = useMemo(() => new Date(conversation.startTime), [ conversation.startTime ]);
  const endTime = new Date(conversation.endTime);
  const totalDuration = differenceInSeconds(endTime, startTime);
  const [ timeElapsed, setTimeElapsed ] = useState(differenceInSeconds(new Date(), startTime));
  const [ warn, setWarn ] = useState(false);
  const [ disable, setDisable ] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const secondsElapsed = differenceInSeconds(new Date(), startTime);
      const warningZone = totalDuration - conversation.latencySeconds - 120;
      const disabledZone = totalDuration - conversation.latencySeconds;

      if (secondsElapsed > warningZone) {
        setWarn(true);
      }

      if (secondsElapsed > disabledZone) {
        setDisable(true);
      }

      setTimeElapsed(secondsElapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [
    conversation.latencySeconds,
    startTime,
    totalDuration
  ]);

  return (
    <Box>
      <Box
        w="100%"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        mb={2}
        mt={10}
      >
        <Box
          minH="34px"
          minW="1px"
        >
          {partner ? (
            <Box
              paddingX={4}
              paddingY={2}
              borderRadius={5}
              bg={theme.colors.main}
            >
              <Text as="small">
                You are connected to
                {" "}

                <Text
                  as="small"
                  bold
                >
                  {partner.name}
                </Text>
              </Text>
            </Box>
          ) : (
            <Box
              paddingX={4}
              paddingY={2}
              borderRadius={5}
              bg={theme.colors.warn}
            >
              <Text as="small">
                No partner yet
              </Text>
            </Box>
          )}
        </Box>

        <Box
          minH="34px"
          minW="1px"
        >
          {(!disable && warn) && (
            <Box
              paddingX={4}
              paddingY={2}
              borderRadius={5}
              bg={theme.colors.warn}
            >
              <Text as="small">
                {`${(totalDuration - conversation.latencySeconds) - differenceInSeconds(new Date(), startTime)}s left to send your final messages`}
              </Text>
            </Box>
          )}

          {(timeElapsed < totalDuration && disable) && (
            <Box
              paddingX={4}
              paddingY={2}
              borderRadius={5}
              bg={theme.colors.lightGrey}
            >
              <Text as="small">
                Receiving final messages
              </Text>
            </Box>
          )}

          {timeElapsed >= totalDuration && (
            <Box
              paddingX={4}
              paddingY={2}
              borderRadius={5}
              bg={theme.colors.lightGrey}
            >
              <Text as="small">
                This session has ended.
              </Text>
            </Box>
          )}

        </Box>
      </Box>

      <TimerWrapper>
        <ProgressBar
          warn={warn}
          disable={disable}
          style={{ width: `${timeElapsed / totalDuration * 100}%` }}
        />

      </TimerWrapper>

    </Box>
  );
};

const TimerWrapper = styled(Box)`
  width: 100%;
  height: 10px;
  border-radius: 20px;
  background: #fafafa;
  position: relative;
`;

const ProgressBar = styled(Box)<{warn: boolean, disable: boolean}>`
  ${({
    theme, warn, disable
  }) => css`
    position: absolute; 
    height: 100%;
    transition: all ease-in-out 0.2s;
    border-radius: 20px;
    z-index: 2;
    border: 1px solid white;
    max-width: 100%;
    background: ${disable ? theme.colors.lightGrey : warn ? theme.colors.warn : theme.colors.main};
  `}
`;
